import type { JsonSchemaType, Object as PortalObject } from '@gmn-portal/backend';
import { useObject, useObjectCategory, useObjectVersions } from '@/core/api';
import type { BackendActivityItem } from '@/core/api/types';
import { type TranslateFn, useIntl } from '@/core/i18n/use-intl';
import {
  ActivityBody,
  ActivityFieldChange,
  ActivityHeader,
  ActivityNote,
} from '@/shared/components/activity';
import { ActivityTimeline } from '@/shared/components/activity/activity-timeline';
import { getFieldPermission } from '@/shared/utils/metadata';

import { ResolvedFieldValue } from './resolved-field-value';

type FileActivityPayload = {
  name?: string | null;
  description?: string | null;
  link?: string | null;
};

function getFieldNameForObject(
  field: string,
  objectSchema?: JsonSchemaType,
  categorySchema?: JsonSchemaType,
  t?: TranslateFn,
  groupKey?: string | null
): string | null {
  // Handle regular fields (not metadata)
  if (!field.startsWith('dynamicMetadata.') && !field.startsWith('categoryMetadata.')) {
    return t
      ? (t(`activity.object.fields.${field}` as unknown as never) as unknown as string)
      : `activity.object.fields.${field}`;
  }

  // Handle metadata fields (both categoryMetadata and dynamicMetadata)
  const [prefix, key] = field.split('.');

  // For categoryMetadata, look in categorySchema; for dynamicMetadata, look in both
  let schemaSource: JsonSchemaType | undefined;
  if (prefix === 'categoryMetadata') {
    schemaSource = categorySchema?.properties?.[key] ? categorySchema : undefined;
  } else {
    // dynamicMetadata - check object schema first, then category schema
    schemaSource =
      (objectSchema?.properties?.[key] && objectSchema) ||
      (categorySchema?.properties?.[key] && categorySchema);
  }

  if (!schemaSource?.properties?.[key]) {
    return groupKey === 'gmn-admin' ? key : null; // Field has been deleted from schema
  }

  if (schemaSource?.properties?.[key]) {
    const permission = getFieldPermission(schemaSource.properties[key], groupKey ?? null);
    if (!permission.read) return null;
  }

  const description = schemaSource.properties[key]?.description;
  if (description) {
    return description;
  }

  return t
    ? (t(`activity.object.fields.${field}` as unknown as never) as unknown as string)
    : `activity.object.fields.${field}`;
}

export interface ObjectActivityProps {
  objectId?: string;
  groupKey?: string | null;
}

export function ObjectActivity({ objectId, groupKey }: ObjectActivityProps) {
  const { t } = useIntl();
  const {
    data: activityData,
    isLoading: isLoadingActivities,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useObjectVersions(objectId!, {
    disabled: !objectId,
    limit: 15,
  });

  const { data: object } = useObject(objectId!);
  const categoryRelation = object?.category as PortalObject['category'] | string | undefined;
  const categoryId =
    typeof categoryRelation === 'object'
      ? categoryRelation?.id
      : (categoryRelation as string | undefined);
  const { data: fetchedCategory } = useObjectCategory(categoryId || '', {
    disabled: !categoryId || typeof categoryRelation === 'object',
  });
  const categorySchema =
    (typeof categoryRelation === 'object'
      ? (categoryRelation?.metadataSchema as JsonSchemaType | undefined)
      : (fetchedCategory?.metadataSchema as JsonSchemaType | undefined)) ?? undefined;

  if (isLoadingActivities) {
    return <div className="text-center text-muted-foreground">{t('activity.loading')}</div>;
  }

  if (!activityData?.pages[0].docs.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  // Flatten all pages of activities
  const allActivities = activityData.pages.flatMap((page) => page.docs);

  // Filter activities to show relevant ones
  const objectSchema = object?.dynamicMetadataSchema as JsonSchemaType | undefined;
  const filteredActivities = allActivities.filter((activity: BackendActivityItem) => {
    if (activity.type === 'created') return !hasNextPage;
    return activity.changes?.some((change) => {
      const activityObjectSchema =
        (activity.schema?.objectDynamicSchema as JsonSchemaType | undefined) || objectSchema;
      const activityCategorySchema =
        (activity.schema?.objectCategorySchema as JsonSchemaType | undefined) || categorySchema;
      const fieldName = getFieldNameForObject(
        change.field,
        activityObjectSchema,
        activityCategorySchema,
        t,
        groupKey
      );
      return fieldName !== null;
    });
  });

  if (!filteredActivities.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  const getFileLabel = (payload?: FileActivityPayload | null) => {
    if (!payload) return t('common.file');
    return payload.name || payload.link || t('common.file');
  };

  return (
    <div className="space-y-4">
      <ActivityTimeline
        activities={filteredActivities.map((activity) => ({
          id: activity.id,
          type: activity.type as 'created' | 'updated',
          timestamp: activity.timestamp,
          user: activity.user,
          changes: activity.changes,
          schema: activity.schema,
        }))}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      >
        {(activity) => {
          if (activity.type === 'created') {
            return (
              <ActivityHeader userId={activity.user} timestamp={activity.timestamp}>
                <span className="text-muted-foreground">{t('activity.object.created')}</span>
              </ActivityHeader>
            );
          }

          const activityObjectSchema =
            (activity.schema?.objectDynamicSchema as JsonSchemaType | undefined) || objectSchema;
          const activityCategorySchema =
            (activity.schema?.objectCategorySchema as JsonSchemaType | undefined) || categorySchema;

          const nodes = (activity.changes ?? [])
            .map((change, index) => {
              if (change.field === 'fileAdded') {
                const label = getFileLabel((change.to || null) as FileActivityPayload | null);
                return (
                  <ActivityNote key={index} action={t('activity.fileAdded')} subject={label} />
                );
              }

              if (change.field === 'fileRemoved') {
                const label = getFileLabel((change.from || null) as FileActivityPayload | null);
                return (
                  <ActivityNote key={index} action={t('activity.fileRemoved')} subject={label} />
                );
              }

              if (change.field === 'fileUpdated') {
                const fromLabel = getFileLabel((change.from || null) as FileActivityPayload | null);
                const toLabel = getFileLabel((change.to || null) as FileActivityPayload | null);
                return (
                  <ActivityNote
                    key={index}
                    action={t('activity.fileUpdatedAction')}
                    from={fromLabel}
                    to={toLabel}
                  />
                );
              }

              const fieldName = getFieldNameForObject(
                change.field,
                activityObjectSchema,
                activityCategorySchema,
                t,
                groupKey
              );
              if (fieldName === null) return null;

              const isMetadata =
                change.field.startsWith('dynamicMetadata.') ||
                change.field.startsWith('categoryMetadata.');

              return (
                <ActivityFieldChange
                  key={index}
                  prefix={isMetadata ? t('activity.metadataField') : undefined}
                  label={fieldName}
                  from={
                    <ResolvedFieldValue
                      field={change.field}
                      value={change.from}
                      objectSchema={activityObjectSchema}
                      categorySchema={activityCategorySchema}
                      category={object?.category}
                      assignedCustomer={object?.assignedCustomer}
                      assignedHelicopterServiceProvider={object?.assignedHelicopterServiceProvider}
                      linkedWindpark={
                        (object as { linkedWindpark?: unknown } | undefined)?.linkedWindpark
                      }
                    />
                  }
                  to={
                    <ResolvedFieldValue
                      field={change.field}
                      value={change.to}
                      objectSchema={activityObjectSchema}
                      categorySchema={activityCategorySchema}
                      category={object?.category}
                      assignedCustomer={object?.assignedCustomer}
                      assignedHelicopterServiceProvider={object?.assignedHelicopterServiceProvider}
                      linkedWindpark={
                        (object as { linkedWindpark?: unknown } | undefined)?.linkedWindpark
                      }
                    />
                  }
                />
              );
            })
            .filter(Boolean);

          return (
            <div>
              <ActivityHeader userId={activity.user} timestamp={activity.timestamp} />
              {nodes.length > 0 ? (
                <ActivityBody>{nodes}</ActivityBody>
              ) : (
                <ActivityBody>
                  <ActivityNote action={t('activity.savedWithoutChanges')} />
                </ActivityBody>
              )}
            </div>
          );
        }}
      </ActivityTimeline>
    </div>
  );
}
